/**
 * The AI panel's card for a document job on this document (R1 P3′).
 *
 * Fed from the Active AI tasks list (`/agent/jobs`, polled by the tray), so
 * it needs no polling of its own. Shows only saved facts: the job's line, its
 * phases (reading, planning, writing, reviewing) built from its progress
 * events, each section it settled — a written one links to itself — and links
 * to the suggestion list and the question list. Cancel while it waits or
 * runs; Dismiss once it has ended.
 *
 * On the document overview (`overview`, R1 UX1) it also shows how the job
 * ended. A finished job that drafted something gets the one-line recap (draft
 * page redesign): "Drafted 3 sections · 3 suggestions to review · 8 questions
 * for your team", "Review suggestions" and "Answer questions", then each
 * section with a "Suggestion ready" pill while its suggestion waits. The
 * recap's long text (what the review left open, the questions) stays off the
 * overview: questions are answered in the list below, and each section's
 * notes belong with its suggestion. A job that failed, was cancelled or wrote
 * nothing keeps its card and the recap text it wrote. The overview reads the
 * document's latest job itself, so the card survives a reload and the tray's
 * one-hour window. The AI panel's card shows no recap: a chat-started job's
 * recap is already in that chat.
 */

import { useCallback, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useInRouterContext, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertCircle, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ActivityPhases } from '@/components/chat/ActivityPhases'
import {
  DocumentJobLinksRow,
  DocumentJobRecapSectionList,
  DocumentJobSectionList,
  DocumentJobStatusIcon,
} from '@/components/DocumentJobProgress'
import { hydratePhasesFromJobSteps } from '@/lib/activity-phases'
import {
  documentJobLine,
  documentJobLinks,
  documentJobPhaseSteps,
  documentJobState,
  draftedSectionCount,
  firstSuggestionHref,
  hasDraftedRecap,
  recapLineParts,
  type SectionLink,
} from '@/lib/document-job-progress'
import { questionProgress } from '@/lib/question-view'
import { cn } from '@/lib/utils'
import { useActiveJobsStore, type ActiveJob } from '@/stores/active-jobs-store'
import { selectDocumentQuestions, useQuestionsStore } from '@/stores/questions-store'

interface PanelDocumentJobProps {
  artifactId: string
  className?: string
  /** The document overview: read the document's latest job and show how it ended (R1 UX1). */
  overview?: boolean
  /**
   * The overview's staged suggestions waiting on this document, in its order
   * (the responses store's list), or null before the page has read them. The
   * recap counts them and pills their sections.
   */
  pendingSuggestions?: ReadonlyArray<{ proposalId: string; sectionKey: string }> | null
}

/** "Review suggestions": opens the first suggestion waiting for review. */
const RECAP_REVIEW_SUGGESTIONS = 'Review suggestions'
/** "Answer questions": brings the overview's question list into view. */
const RECAP_ANSWER_QUESTIONS = 'Answer questions'

const REMARK_PLUGINS = [remarkGfm]

function isEnded(job: ActiveJob): boolean {
  return job.status !== 'pending' && job.status !== 'running'
}

export function PanelDocumentJob({ artifactId, className, overview = false, pendingSuggestions = null }: PanelDocumentJobProps) {
  const job = useActiveJobsStore((s) =>
    overview ? s.getOverviewDocumentJob(artifactId) : s.getDocumentJobForArtifact(artifactId)
  )
  const loadLatestDocumentJob = useActiveJobsStore((s) => s.loadLatestDocumentJob)
  const inRouter = useInRouterContext()

  useEffect(() => {
    if (overview) void loadLatestDocumentJob(artifactId)
  }, [overview, artifactId, loadLatestDocumentJob])

  // A job that ends while the overview is open may reach the tray a moment
  // before its recap is written (a failed job's recap follows its last
  // write): ask for the latest once more, once per job.
  const awaitingRecap = overview && job && isEnded(job) && !job.document?.recap ? job.jobId : null
  const mountedAwaitingRef = useRef(awaitingRecap)
  const askedAgainRef = useRef<string | null>(null)
  useEffect(() => {
    if (!awaitingRecap || awaitingRecap === mountedAwaitingRef.current || awaitingRecap === askedAgainRef.current) return
    askedAgainRef.current = awaitingRecap
    void loadLatestDocumentJob(artifactId)
  }, [awaitingRecap, artifactId, loadLatestDocumentJob])

  if (!job) return null
  const showRecapLine = overview && hasDraftedRecap(job)
  const recap = overview && isEnded(job) && !showRecapLine ? job.document?.recap ?? null : null
  const card = { job, recap, className, recapLine: showRecapLine ? { artifactId, pendingSuggestions } : null }
  return inRouter
    ? <RoutedDocumentJobCard {...card} />
    : <DocumentJobCard {...card} onNavigate={() => {}} />
}

interface CardProps {
  job: ActiveJob
  /** The recap text to show (overview, a job that ended without drafting anything), or null. */
  recap: string | null
  /** The overview's one-line recap of a finished job that drafted something, or null. */
  recapLine: { artifactId: string; pendingSuggestions: PanelDocumentJobProps['pendingSuggestions'] } | null
  className?: string | undefined
}

function RoutedDocumentJobCard(props: CardProps) {
  const navigate = useNavigate()
  const handleNavigate = useCallback((href: string) => void navigate(href), [navigate])
  return <DocumentJobCard {...props} onNavigate={handleNavigate} />
}

function DocumentJobCard({ recapLine, ...props }: CardProps & { onNavigate: (href: string) => void }) {
  return recapLine
    ? <DocumentJobRecap {...props} {...recapLine} />
    : <DocumentJobProgressCard {...props} />
}

function DocumentJobProgressCard({ job, recap, className, onNavigate }: Omit<CardProps, 'recapLine'> & { onNavigate: (href: string) => void }) {
  const cancelDocumentJob = useActiveJobsStore((s) => s.cancelDocumentJob)
  const dismissJob = useActiveJobsStore((s) => s.dismissJob)
  const active = job.status === 'pending' || job.status === 'running'
  const steps = documentJobPhaseSteps(job.document, { ended: !active })
  // Before its first saved fact the job's line already says "Started": the
  // activity block would only repeat it twice more (BV2), so it waits for a fact.
  const phases = hydratePhasesFromJobSteps(steps, { running: job.status === 'running' && steps.length > 0 })

  const handleCancel = async () => {
    const result = await cancelDocumentJob(job.jobId)
    if (!result.ok) toast.error(result.message)
  }

  return (
    <div className={cn('rounded-lg bg-muted/40 px-3 py-2 space-y-1.5', className)} data-testid="panel-document-job">
      <div className="flex items-center gap-2">
        <span className="shrink-0">
          <DocumentJobStatusIcon job={job} />
        </span>
        <span className={cn('min-w-0 flex-1 truncate text-meta', job.status === 'failed' && 'text-destructive')}>
          {documentJobLine(job)}
        </span>
        {active ? (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => void handleCancel()}>
            Cancel
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label="Dismiss"
            onClick={() => dismissJob(job.jobId)}
          >
            <X className="h-4 w-4 text-subtle" />
          </Button>
        )}
      </div>
      {phases.phases.length > 0 && <ActivityPhases phases={phases} isStreaming={job.status === 'running'} />}
      {recap && (
        <div className="chat-markdown prose prose-sm max-w-none dark:prose-invert" data-testid="document-job-recap">
          <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{recap}</ReactMarkdown>
        </div>
      )}
      <DocumentJobSectionList job={job} onNavigate={onNavigate} />
      <DocumentJobLinksRow job={job} onNavigate={onNavigate} />
    </div>
  )
}

// ===================
// The overview's one-line recap (draft page redesign)
// ===================

interface DocumentJobRecapProps {
  job: ActiveJob
  artifactId: string
  pendingSuggestions: PanelDocumentJobProps['pendingSuggestions']
  className?: string | undefined
  onNavigate: (href: string) => void
}

function DocumentJobRecap({ job, artifactId, pendingSuggestions, className, onNavigate }: DocumentJobRecapProps) {
  const dismissJob = useActiveJobsStore((s) => s.dismissJob)
  const requestFocus = useQuestionsStore((s) => s.requestFocus)
  // Live: the overview's question list, so the count drops as questions are answered.
  const waitingQuestions = useQuestionsStore((s) => questionProgress(selectDocumentQuestions(s, artifactId).asks).waiting)

  const { sections, suggestions: jobSuggestions } = documentJobLinks(job)
  const isSingleDocument = job.document?.shape === 'document_row'
  const pendingKeys = pendingSuggestions ? new Set(pendingSuggestions.map((item) => item.sectionKey)) : null
  // Before the page has read its suggestions, the job's own count stands in.
  const suggestionCount = pendingSuggestions ? pendingSuggestions.length : jobSuggestions?.count ?? 0
  const reviewHref = suggestionCount > 0
    ? firstSuggestionHref(job, pendingSuggestions ? pendingSuggestions.map((item) => item.sectionKey) : null)
    : null
  const parts = recapLineParts({ drafted: draftedSectionCount(job), suggestions: suggestionCount, questions: waitingQuestions })
  const isPartial = documentJobState(job) === 'partial'

  const isPending = (section: SectionLink): boolean => {
    if (!pendingKeys) return section.result === 'suggested'
    // A single document's suggestion is the whole document: every section written into it waits on it.
    if (isSingleDocument) return pendingKeys.size > 0 && section.href !== null
    return pendingKeys.has(section.sectionKey)
  }

  return (
    <div className={cn('space-y-3 rounded-xl bg-muted/40 p-3', className)} data-testid="panel-document-job">
      <div className="flex flex-wrap items-center gap-2.5">
        <span
          className={cn(
            'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            isPartial
              ? 'bg-[hsl(var(--status-warning)/0.12)] text-[hsl(var(--status-warning))]'
              : 'bg-[hsl(var(--status-success)/0.12)] text-[hsl(var(--status-success))]'
          )}
        >
          {isPartial ? <AlertCircle className="h-3.5 w-3.5" aria-hidden /> : <Check className="h-3.5 w-3.5" aria-hidden />}
        </span>
        <p className="min-w-[220px] flex-1 text-sm font-medium text-foreground" data-testid="document-job-recap-line">
          {parts.map((part, index) => (
            <span key={part}>
              {index > 0 && <span className="text-subtle">{' · '}</span>}
              {part}
            </span>
          ))}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {reviewHref && (
            <Button size="sm" onClick={() => onNavigate(reviewHref)}>
              {RECAP_REVIEW_SUGGESTIONS}
            </Button>
          )}
          {waitingQuestions > 0 && (
            <Button variant="outline" size="sm" onClick={() => requestFocus(artifactId)}>
              {RECAP_ANSWER_QUESTIONS}
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Dismiss" onClick={() => dismissJob(job.jobId)}>
            <X className="h-4 w-4 text-subtle" />
          </Button>
        </div>
      </div>
      {isPartial && <p className="text-meta">{documentJobLine(job)}</p>}
      <DocumentJobRecapSectionList sections={sections} isPending={isPending} onNavigate={onNavigate} />
    </div>
  )
}
